import React, { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { checkArray, converDropdownData, imageUrl, shortenandHoverString, transportArray } from '../../../../Components/helper';
import { useProductListingState } from '../RangePlanProductListingProvider';
import noImage from '../../../../assets/images/no-image.png';
import { useProductEditNavigation } from '../../../../hooks/useProductEditNavigation';
import CommonImageComponent from '../../../../Components/Utility/CommonImage';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import Loader from '../../../../Components/Utility/Loader/Loader';
import RenderProductInformationColouredCard from './RenderProductInformationColouredCard';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';

export default function ProductInfo(props) {
  const {
    handleSelection,
    selectedIds,
    isSelectAll,
    handlePageNextInScroll,
    loadNextData,
    colouredCardFaces,
    colouredFaces,
    selectedCardAttributes,
    handleCombinationSelection,
    selectedCombinations,
  } = props;
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { CheckActionPermission } = useComponent();

  const {
    rangePlanProvider: { productsNumber, productsList },
    lengthEnabled,
  } = useProductListingState();
  const reducers = useSelector((redState) => redState);
  const {
    ProductListData: { customAttributes },
    SystemMastersDetails: { subBrandAccess, pcpCustomAttribute },
    Users: { logInRights },
  } = reducers;
  const rangePlanAddEditAccess = CheckActionPermission(logInRights, 'rangePlan', 'default', 'addEdit');

  const [state, setState] = useState({
    dynamicFields: [],
  });
  const { dynamicFields } = state;
  const { goToProductEdit } = useProductEditNavigation();
  const secondCondition = (col) => {
    if (col?.colId?.type === 'text' && col?.valueText) return col.valueText;
    if (col?.colId?.type === 'date-picker' && col?.valueText) {
      const fmt = col?.colId?.datePickerConfig?.dateFormat || 'DD-MMM-YYYY';
      const parsed = moment(col.valueText, 'YYYY-MM-DD', true);
      return parsed.isValid() ? parsed.format(fmt) : col.valueText;
    }
    return '--';
  };

  /**
   * Renders a product's picks for one multi custom attribute as a comma separated list of option
   * key names, matching how the Range Plan export and upload address these values.
   * @param {Object} product - Product row from the listing response.
   * @param {Object} column - Multi custom attribute master the column maps to.
   * @returns {string} Comma separated option key names, or '--' when nothing is selected.
   */
  const multiCustomCellValue = (product, column) => {
    const entry = product?.multiCustomAttribute?.find((item) => item?.colId?._id === column?._id);
    const names = checkArray(entry?.valId) ? entry.valId.map((option) => option?.keyName).filter(Boolean) : [];
    return names.length > 0 ? names.join(', ') : '--';
  };

  const handleProductEdit = (id) => {
    goToProductEdit(id);
  };
  useEffect(() => {
    const callFn = async () => {
      dispatch(systemMasterAction.getSubBrandAccess());
      dispatch(systemMasterAction.getMultiCustomAttribute({ isActive: true }));
    };
    callFn();
  }, [dispatch]);

  useEffect(() => {
    const callFun = async () => {
      const finalData = await converDropdownData(customAttributes);
      setState((prevState) => ({
        ...prevState,
        dynamicFields: finalData,
      }));
    };
    if (Array.isArray(customAttributes)) {
      callFun();
    }
  }, [customAttributes]);

  const imgSrc = (element) => {
    if (!element?.image || isEmpty(element?.image) || element?.image === null) {
      return noImage;
    }
    if (element.image.includes('https://') || element.image.includes('http://')) {
      return element.image;
    }
    return `${imageUrl}/${element.image}`;
  };
  return (
    <>
      {colouredCardFaces ? (
        <Container fluid>
          <InfiniteScroll
            dataLength={productsList?.length}
            hasMore={productsList?.length < productsNumber}
            next={handlePageNextInScroll}
            loader={loadNextData && <Loader />}
            scrollableTarget="ProductInfoRPColouredCardContainer"
            className="fixed-col-container rp-table-container"
          >
            <RenderProductInformationColouredCard
              productsList={productsList}
              selectedCardAttributes={selectedCardAttributes}
              colouredFaces={colouredFaces}
              handleSelection={handleSelection}
              selectedIds={selectedIds}
              handleCombinationSelection={handleCombinationSelection}
              selectedCombinations={selectedCombinations}
            />
          </InfiniteScroll>
        </Container>
      ) : (
        <div id="ProductInfoRPTableContainer" className="rp-scroll-container">
          <InfiniteScroll
            dataLength={productsList?.length}
            hasMore={productsList?.length < productsNumber}
            next={handlePageNextInScroll}
            loader={loadNextData && <Loader />}
            scrollableTarget="ProductInfoRPTableContainer"
            className="fixed-col-container rp-table-container"
          >
            <table id="RPTable" className="table common-custom-table fixed-header mb-0">
              <thead>
                <tr>
                  <th className="w-50px">
                    <div className="common-checkbox margin-auto">
                      <input
                        type="checkbox"
                        onChange={(e) => handleSelection(e.target.checked)}
                        name="check_all_samples_product_info"
                        id="check_all_samples_product_info"
                        checked={isSelectAll}
                        disabled={!productsList?.length}
                      />
                      <label htmlFor="check_all_samples_product_info" className="chklabel" />
                    </div>
                  </th>

                  <th className="w-80px">{t('RANGE_PLANS.PRODUCT_LIST.IMAGE')}</th>
                  <th className="w-200px first-fix-col">{t('RANGE_PLANS.PRODUCT_LIST.PRODUCT_NUMBER')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.DESCRIPTION')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.BRAND')}</th>
                  {subBrandAccess && <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.SUB_BRAND')}</th>}
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.SEASON')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.MONTH')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.YEAR')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.PRODUCT_TYPE')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.CATEGORY_1')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.CATEGORY_2')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.CATEGORY_3')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.CATEGORY_4')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.LICENSOR')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.LICENSE')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.LIKENESS')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_TABLE.GRADING_GROUP')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.SIZE_RANGE')}</th>
                  {lengthEnabled && <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_TABLE.LENGTH')}</th>}
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.COLOURWAY_NAME')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.SUPPLIER')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.FACTORY')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.PRODUCT_TYPE_PROCESS')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.TRANSPORT')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.NO_COLOURWAYS')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.ORIGINAL_SUBMISSION')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.ORIGINAL_DOWNLOAD')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.MOST_RECENT_SUBMISSION')}</th>
                  <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.MOST_RECENT_DOWNLOAD')}</th>
                  {checkArray(dynamicFields) &&
                    dynamicFields?.map((col) => {
                      return (
                        <th key={col?._id} className="w-150px">
                          {col?.keyName}
                        </th>
                      );
                    })}
                  {checkArray(pcpCustomAttribute) &&
                    pcpCustomAttribute?.map((col) => {
                      return (
                        <th key={col?._id} className="w-150px">
                          {col?.keyName}
                        </th>
                      );
                    })}
                  <th className="w-150px last-fix-col">{t('RANGE_PLANS.PRODUCT_LIST.STATUS')}</th>
                </tr>
              </thead>

              {productsList?.length > 0 ? (
                productsList?.map((ele, i) => {
                  const productImage = imgSrc(ele);
                  return (
                    <tbody key={ele?._id}>
                      <tr>
                        <td>
                          <div className="common-checkbox margin-auto">
                            <input
                              onChange={() => {
                                handleSelection(ele?._id);
                              }}
                              name={`checks_${i}`}
                              id={`checks_${i}`}
                              type="checkbox"
                              checked={selectedIds?.filter((val) => val === ele?._id)?.length > 0}
                            />
                            <label htmlFor={`checks_${i}`} className="chklabel" />
                          </div>
                        </td>

                        <td>
                          <CommonImageComponent productImage={productImage} />
                        </td>
                        <td className="first-fix-col">
                          <div className="d-flex align-items-center">
                            {shortenandHoverString(ele?.number || '--', 30)}
                            {rangePlanAddEditAccess && (
                              <button type="button" className="btn ml-auto" aria-label="Edit" onClick={() => handleProductEdit(ele?._id)}>
                                <i className="icon-Edit-1 f-22" />
                              </button>
                            )}
                            <Link
                              target="_blank"
                              to={`/development-order/${ele?._id}/design-details/list`}
                              className={!rangePlanAddEditAccess ? 'text-decoration-none ml-auto' : 'text-decoration-none'}
                            >
                              <span className="icon-redirect f-20" />
                            </Link>
                          </div>
                        </td>
                        <td>{shortenandHoverString(ele?.description, 40)}</td>
                        <td>{ele?.brandId?.name || '--'}</td>
                        {subBrandAccess && <td>{ele?.subBrandId?.name || '--'}</td>}
                        <td>{ele?.seasonId?.name || '--'}</td>
                        <td>{ele?.month || '--'}</td>
                        <td>{ele?.year || '--'}</td>
                        <td>{ele?.supplierType?.name || '--'}</td>
                        <td>{ele?.catOneId?.name || '--'}</td>
                        <td>{ele?.catTwoId?.name || '--'}</td>
                        <td>{ele?.catThreeId?.name || '--'}</td>
                        <td>{ele?.catFourId?.name || '--'}</td>
                        <td>{ele?.licensorId?.name || '--'}</td>
                        <td>{ele?.licenseId?.name || '--'}</td>
                        <td>{ele?.likenessId?.name || '--'}</td>
                        <td>{shortenandHoverString(ele?.gradingGroups?.map((data) => data?.name)?.join(', ') || '--', 40)}</td>
                        <td>{checkArray(ele?.sizeRanges) && shortenandHoverString(ele?.sizeRanges?.join(', '), 40)}</td>
                        {lengthEnabled && <td>{ele?.legLengths?.map((data) => data?.name).join(', ') || '--'}</td>}
                        <td>{checkArray(ele?.colourLibraries) && shortenandHoverString(ele?.colourLibraries?.map((data) => data?.name)?.join(', '), 40)}</td>
                        <td>{checkArray(ele?.suppliers) && shortenandHoverString(ele?.suppliers?.join(', '), 40)}</td>
                        <td>{shortenandHoverString(ele?.supplierFactories?.map((data) => data?.factoryName)?.join(', '), 40) || '--'}</td>
                        <td>{ele?.productTypeProcesses?.map((data) => data?.name)?.join(', ') || '--'}</td>
                        <td>{transportArray?.find((data) => data?.name === ele?.transport)?.label || '--'} </td>
                        <td>{ele?.colourwaysCount}</td>
                        <td>
                          {ele?.originalSubmitBy}
                          <br />
                          {ele?.originalSubmitDate}
                        </td>
                        <td>
                          {ele?.originalDownloadBy}
                          <br />
                          {ele?.originalDownloadDate}
                        </td>
                        <td>
                          {ele?.recentSubmitBy}
                          <br />
                          {ele?.recentSubmitDate}
                        </td>
                        <td>
                          {ele?.recentDownloadBy} <br />
                          {ele?.recentDownloadDate}
                        </td>

                        {checkArray(dynamicFields) &&
                          dynamicFields.map((col) => {
                            const matchingColumn = ele?.dynamicColumn?.find((dynCol) => dynCol?.colId?._id === col._id);
                            return (
                              <td key={col?._id}>
                                {matchingColumn?.valueId ? shortenandHoverString(matchingColumn?.valueId?.keyName, 20) : secondCondition(matchingColumn)}
                              </td>
                            );
                          })}

                        {checkArray(pcpCustomAttribute) &&
                          pcpCustomAttribute.map((col) => {
                            return <td key={col?._id}>{shortenandHoverString(multiCustomCellValue(ele, col), 20)}</td>;
                          })}

                        <td className="last-fix-col">{ele?.status?.name || '--'}</td>
                      </tr>
                    </tbody>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="100%" className="text-center">
                    {t('RANGE_PLANS.PRODUCT_LIST.NO_RECORDS_FOUND')}
                  </td>
                </tr>
              )}
            </table>
          </InfiniteScroll>
        </div>
      )}
    </>
  );
}
